import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { CalendarClock, Check, ChevronsUpDown, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { listTrainerCalendarStatuses } from "@/lib/calendar.functions";
import { listTrainers, saveTrainer, saveTeachingBlock, deleteTeachingBlock, deleteTrainer } from "@/lib/trainers.functions";
import { getMe } from "@/lib/core.functions";
import { PageHeader, EmptyState } from "@/components/AppShell";
import { DAY_NAMES, formatTime, shortDay, SCHEDULE_TZ_LABEL, SCHEDULE_TZ_SHORT } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { relOne } from "@/lib/relations";

export const Route = createFileRoute("/_authenticated/trainers")({
  head: () => ({
    meta: [
      { title: "Trainers | Leadership Edge Scheduling" },
      { name: "description", content: "Manage trainers, their contact details, and the weekly teaching blocks used for assignment." },
      { property: "og:title", content: "Trainers | Leadership Edge Scheduling" },
      { property: "og:description", content: "Trainers and their weekly teaching blocks." },
    ],
  }),
  component: TrainersPage,
});

interface TrainerForm {
  id: string;
  /** Carried through untouched — user details are managed in User management. */
  full_name: string;
  email: string;
  is_active: boolean;
  notes: string;
  authorization_mode: "all" | "except";
  excluded_course_ids: string[];
}

interface CourseOption {
  id: string;
  name: string;
  code?: string | null;
}

/** Multi-select with search and removable chips for the "cannot teach" list. */
function CourseExclusionPicker({
  courses,
  selected,
  onChange,
}: {
  courses: CourseOption[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const chosen = courses.filter((c) => selected.includes(c.id));

  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="soft" className="w-full justify-between" type="button">
            <span className="truncate">
              {selected.length === 0
                ? "Select courses"
                : `${selected.length} course${selected.length === 1 ? "" : "s"} excluded`}
            </span>
            <ChevronsUpDown className="opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[min(28rem,90vw)] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search courses…" />
            <CommandList>
              <CommandEmpty>No course matches that search.</CommandEmpty>
              <CommandGroup>
                {courses.map((course) => {
                  const isOn = selected.includes(course.id);
                  return (
                    <CommandItem
                      key={course.id}
                      value={`${course.name} ${course.code ?? ""}`}
                      onSelect={() => toggle(course.id)}
                    >
                      <Check className={isOn ? "opacity-100" : "opacity-0"} />
                      <span className="truncate">{course.name}</span>
                      {course.code ? (
                        <span className="ml-auto font-mono text-xs text-muted-foreground">{course.code}</span>
                      ) : null}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {chosen.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {chosen.map((course) => (
            <li
              key={course.id}
              className="flex items-center gap-1 rounded-full bg-destructive/10 px-2.5 py-1 text-xs font-medium text-destructive"
            >
              {course.name}
              <button
                type="button"
                aria-label={`Remove ${course.name}`}
                onClick={() => toggle(course.id)}
                className="rounded-full p-0.5 hover:bg-destructive/20"
              >
                <X className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

interface BlockRow {
  key: string;
  id?: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  label: string;
}

interface BlockForm {
  trainer_id: string;
  trainer_name: string;
  rows: BlockRow[];
  removedIds: string[];
}

let rowSeq = 0;
function newRow(): BlockRow {
  rowSeq += 1;
  return { key: `row-${rowSeq}`, day_of_week: 1, start_time: "09:00", end_time: "12:00", label: "" };
}


function TrainersPage() {
  const queryClient = useQueryClient();
  const fetchMe = useServerFn(getMe);
  const fetchTrainers = useServerFn(listTrainers);
  const save = useServerFn(saveTrainer);
  
  const saveBlock = useServerFn(saveTeachingBlock);
  const removeBlock = useServerFn(deleteTeachingBlock);
  const removeTrainer = useServerFn(deleteTrainer);

  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => fetchMe() });
  const { data, isPending } = useQuery({ queryKey: ["trainers"], queryFn: () => fetchTrainers() });
  const fetchCal = useServerFn(listTrainerCalendarStatuses);
  const isAdminEarly = me?.isAdmin ?? false;
  const calQuery = useQuery({
    queryKey: ["trainer-calendar-status"],
    queryFn: () => fetchCal(),
    enabled: isAdminEarly,
    staleTime: 60_000,
  });
  const calById = new Map((calQuery.data?.statuses ?? []).map((s) => [s.trainerId, s]));

  const [form, setForm] = useState<TrainerForm | null>(null);
  const [blockForm, setBlockForm] = useState<BlockForm | null>(null);
  const [search, setSearch] = useState("");



  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["trainers"] });
    queryClient.invalidateQueries({ queryKey: ["courses"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  };

  const saveMutation = useMutation({
    // Name, email and active state are sent back unchanged: they belong to User management.
    mutationFn: (payload: TrainerForm) =>
      save({
        data: {
          id: payload.id,
          full_name: payload.full_name,
          email: payload.email,
          is_active: payload.is_active,
          notes: payload.notes.trim() ? payload.notes.trim() : null,
          authorization_mode: payload.authorization_mode,
          excluded_course_ids: payload.authorization_mode === "except" ? payload.excluded_course_ids : [],
        },
      }),
    onSuccess: () => {
      toast.success("Trainer settings saved.");
      setForm(null);
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });


  const blockMutation = useMutation({
    mutationFn: async (payload: BlockForm) => {
      for (const id of payload.removedIds) {
        await removeBlock({ data: { id } });
      }
      for (const row of payload.rows) {
        await saveBlock({
          data: {
            ...(row.id ? { id: row.id } : {}),
            trainer_id: payload.trainer_id,
            day_of_week: row.day_of_week,
            start_time: row.start_time,
            end_time: row.end_time,
            label: row.label.trim() ? row.label.trim() : null,
            effective_from: null,
            effective_to: null,
          },
        });
      }
      return { ok: true };
    },
    onSuccess: () => {
      toast.success("Teaching blocks saved in Phoenix time.");
      setBlockForm(null);
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });


  const trainerDeleteMutation = useMutation({
    mutationFn: (id: string) => removeTrainer({ data: { id } }),
    onSuccess: () => {
      toast.success("Trainer removed.");
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const blockDeleteMutation = useMutation({
    mutationFn: (id: string) => removeBlock({ data: { id } }),
    onSuccess: () => {
      toast.success("Teaching block removed.");
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const trainers = data?.trainers ?? [];
  const blocks = data?.blocks ?? [];
  const links = data?.links ?? [];
  const catalog: CourseOption[] = data?.courses ?? [];
  const exclusions = data?.exclusions ?? [];
  const isAdmin = me?.isAdmin ?? false;

  const term = search.trim().toLowerCase();
  const visible = term
    ? trainers.filter((t) =>
        `${t.full_name ?? ""} ${t.email ?? ""} ${t.notes ?? ""}`.toLowerCase().includes(term),
      )
    : trainers;

  /** Open the timing/availability editor for an existing trainer (a user with Trainer on). */
  const openBlocks = (trainerId: string, trainerName: string) => {
    const own = blocks.filter((b) => b.trainer_id === trainerId);
    setBlockForm({
      trainer_id: trainerId,
      trainer_name: trainerName,
      removedIds: [],
      rows:
        own.length > 0
          ? own.map((b) => ({
              key: `existing-${b.id}`,
              id: b.id,
              day_of_week: b.day_of_week,
              start_time: b.start_time.slice(0, 5),
              end_time: b.end_time.slice(0, 5),
              label: b.label ?? "",
            }))
          : [newRow()],
    });
  };



  return (
    <>
      <PageHeader
        eyebrow="People"
        title="Trainers"
        description={`Everyone with the Trainer switch on in User management appears here. Teaching blocks are standing weekly windows in ${SCHEDULE_TZ_LABEL}.`}
      />

      <div className="mb-4 max-w-sm">
        <Label htmlFor="trainer-search" className="sr-only">
          Search trainers
        </Label>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="trainer-search"
            className="pl-9"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {isAdmin ? (
        <CalendarSummary
          loading={calQuery.isFetching}
          configured={calQuery.data?.configured ?? true}
          needing={(calQuery.data?.statuses ?? [])
            .filter((s) => s.status !== "connected")
            .map((s) => ({ ...s, name: trainers.find((t) => t.id === s.trainerId)?.full_name ?? "Trainer" }))}
          total={calQuery.data?.statuses.length ?? 0}
          onRefresh={() => calQuery.refetch()}
        />
      ) : null}

      {isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : trainers.length === 0 ? (
        <EmptyState title="No trainers yet" body="Turn on the Trainer switch for someone in User management, then set their weekly teaching blocks here." />
      ) : visible.length === 0 ? (
        <EmptyState title="No match" body="No trainer matches that search. Try a different name or email." />
      ) : (
        <div className="space-y-4">
          {visible.map((trainer) => {
            const own = blocks.filter((b) => b.trainer_id === trainer.id);
            const excludedIds = exclusions
              .filter((e) => e.trainer_id === trainer.id)
              .map((e) => e.course_id);
            const cannotTeach = catalog.filter((c) => excludedIds.includes(c.id));
            return (
              <article key={trainer.id} className="panel p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="font-display text-xl text-foreground">{trainer.full_name}</h2>
                    <p className="mt-1 font-mono text-xs text-muted-foreground">{trainer.email}</p>
                    {isAdmin && calQuery.data?.configured ? (
                      <CalendarBadge status={calById.get(trainer.id)} loading={calQuery.isPending} />
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {!trainer.is_active ? (
                      <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                        Inactive
                      </span>
                    ) : null}
                    {isAdmin ? (
                      <>
                        <Button
                          variant="soft"
                          size="sm"
                          onClick={() =>
                            setForm({
                              id: trainer.id,
                              full_name: trainer.full_name,
                              email: trainer.email,
                              notes: trainer.notes ?? "",
                              is_active: trainer.is_active,
                              authorization_mode:
                                trainer.authorization_mode === "except" ? "except" : "all",
                              excluded_course_ids: exclusions
                                .filter((e) => e.trainer_id === trainer.id)
                                .map((e) => e.course_id),
                            })
                          }
                        >
                          <Pencil /> Trainer settings
                        </Button>
                        <Button
                          variant="soft"
                          size="sm"
                          onClick={() => openBlocks(trainer.id, trainer.full_name)}
                        >
                          <CalendarClock /> Teaching blocks
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          disabled={trainerDeleteMutation.isPending}
                          onClick={() => {
                            if (
                              window.confirm(
                                `Remove ${trainer.full_name} as a trainer? They will no longer be scheduled. Their user account and past sessions stay in place.`,
                              )
                            )
                              trainerDeleteMutation.mutate(trainer.id);
                          }}
                        >
                          <Trash2 /> Delete
                        </Button>
                      </>
                    ) : null}
                  </div>
                </div>

                <div className="mt-4 grid gap-5 border-t border-border pt-4 sm:grid-cols-2">
                  <div>
                    <p className="eyebrow">Teaching blocks</p>
                    {own.length === 0 ? (
                      <p className="mt-2 text-sm text-muted-foreground">None set. This trainer cannot be assigned yet.</p>
                    ) : (
                      <ul className="mt-2 space-y-1.5">
                        {own.map((block) => (
                          <li key={block.id} className="flex items-center justify-between gap-2 text-sm">
                            <span className="text-foreground">
                              <span className="font-mono text-xs text-accent">{shortDay(block.day_of_week)}</span>{" "}
                              {formatTime(block.start_time)}-{formatTime(block.end_time)} <span className="text-xs text-muted-foreground">{SCHEDULE_TZ_SHORT}</span>
                              {block.label ? <span className="text-muted-foreground"> · {block.label}</span> : null}
                            </span>
                            {isAdmin ? (
                              <button
                                className="text-xs text-muted-foreground underline-offset-2 hover:text-destructive hover:underline"
                                onClick={() => blockDeleteMutation.mutate(block.id)}
                              >
                                remove
                              </button>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div>
                    <p className="eyebrow">Course teaching authorization</p>
                    {trainer.authorization_mode !== "except" ? (
                      <p className="mt-2 text-sm text-foreground">Authorized to teach all courses.</p>
                    ) : cannotTeach.length === 0 ? (
                      <p className="mt-2 text-sm text-muted-foreground">
                        Authorized to teach all courses except: none selected yet.
                      </p>
                    ) : (
                      <>
                        <p className="mt-2 text-sm text-foreground">
                          Authorized to teach all courses except {cannotTeach.length} course
                          {cannotTeach.length === 1 ? "" : "s"}.
                        </p>
                        <ul className="mt-2 flex flex-wrap gap-1.5">
                          {cannotTeach.map((course) => (
                            <li
                              key={course.id}
                              className="rounded-full bg-destructive/10 px-2.5 py-1 text-xs font-medium text-destructive"
                            >
                              {course.name}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                    {links.filter((l) => l.trainer_id === trainer.id).length > 0 ? (
                      <p className="mt-2 text-xs text-muted-foreground">
                        Preferred courses:{" "}
                        {links
                          .filter((l) => l.trainer_id === trainer.id)
                          .map((l) => relOne(l.courses)?.name ?? "Course")
                          .join(", ")}
                      </p>
                    ) : null}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Dialog open={form !== null} onOpenChange={(open) => !open && setForm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Trainer settings{form ? ` · ${form.full_name}` : ""}</DialogTitle>
            <DialogDescription>
              Name, email, role and the Trainer switch are managed in User management. Only trainer
              settings can be changed here.
            </DialogDescription>
          </DialogHeader>

          {form ? (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="trainer-notes">Scheduling notes</Label>
                <Textarea
                  id="trainer-notes"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </div>
              <div className="space-y-3 rounded-md border border-border px-3 py-3">
                <p className="eyebrow">Course teaching authorization</p>
                <RadioGroup
                  value={form.authorization_mode}
                  onValueChange={(value) =>
                    setForm({ ...form, authorization_mode: value === "except" ? "except" : "all" })
                  }
                  className="gap-2"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem id="auth-all" value="all" />
                    <Label htmlFor="auth-all" className="cursor-pointer font-normal">
                      Authorized to teach all courses
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem id="auth-except" value="except" />
                    <Label htmlFor="auth-except" className="cursor-pointer font-normal">
                      Authorized to teach all courses except
                    </Label>
                  </div>
                </RadioGroup>

                {form.authorization_mode === "except" ? (
                  <div className="space-y-1.5">
                    <Label>Courses this Dev Pro cannot teach</Label>
                    <CourseExclusionPicker
                      courses={catalog}
                      selected={form.excluded_course_ids}
                      onChange={(ids) => setForm({ ...form, excluded_course_ids: ids })}
                    />
                  </div>
                ) : null}
              </div>

            </div>
          ) : null}

          <DialogFooter>
            <Button variant="soft" onClick={() => setForm(null)}>
              Cancel
            </Button>
            <Button
              variant="brass"
              disabled={saveMutation.isPending}
              onClick={() => form && saveMutation.mutate(form)}
            >
              {saveMutation.isPending ? "Saving…" : "Save settings"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={blockForm !== null} onOpenChange={(open) => !open && setBlockForm(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Teaching blocks{blockForm ? ` · ${blockForm.trainer_name}` : ""}</DialogTitle>
            <DialogDescription>
              Add one or more standing weekly windows. All times are {SCHEDULE_TZ_LABEL}.
            </DialogDescription>
          </DialogHeader>

          {blockForm ? (
            <div className="space-y-3">
              {blockForm.rows.map((row, index) => (
                <div key={row.key} className="rounded-md border border-border p-3">
                  <div className="grid gap-3 sm:grid-cols-[1.2fr_0.9fr_0.9fr_auto]">
                    <div className="space-y-1.5">
                      <Label>Day</Label>
                      <Select
                        value={String(row.day_of_week)}
                        onValueChange={(value) =>
                          setBlockForm({
                            ...blockForm,
                            rows: blockForm.rows.map((r, i) =>
                              i === index ? { ...r, day_of_week: Number(value) } : r,
                            ),
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DAY_NAMES.map((day, dayIndex) => (
                            <SelectItem key={day} value={String(dayIndex)}>
                              {day}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor={`block-start-${row.key}`}>Starts ({SCHEDULE_TZ_SHORT})</Label>
                      <Input
                        id={`block-start-${row.key}`}
                        type="time"
                        value={row.start_time}
                        onChange={(e) =>
                          setBlockForm({
                            ...blockForm,
                            rows: blockForm.rows.map((r, i) =>
                              i === index ? { ...r, start_time: e.target.value } : r,
                            ),
                          })
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor={`block-end-${row.key}`}>Ends ({SCHEDULE_TZ_SHORT})</Label>
                      <Input
                        id={`block-end-${row.key}`}
                        type="time"
                        value={row.end_time}
                        onChange={(e) =>
                          setBlockForm({
                            ...blockForm,
                            rows: blockForm.rows.map((r, i) =>
                              i === index ? { ...r, end_time: e.target.value } : r,
                            ),
                          })
                        }
                      />
                    </div>
                    <div className="flex items-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Remove block"
                        className="text-destructive hover:bg-destructive/10"
                        onClick={() =>
                          setBlockForm({
                            ...blockForm,
                            rows: blockForm.rows.filter((_, i) => i !== index),
                            removedIds: row.id ? [...blockForm.removedIds, row.id] : blockForm.removedIds,
                          })
                        }
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1.5">
                    <Label htmlFor={`block-label-${row.key}`}>Label</Label>
                    <Input
                      id={`block-label-${row.key}`}
                      value={row.label}
                      onChange={(e) =>
                        setBlockForm({
                          ...blockForm,
                          rows: blockForm.rows.map((r, i) => (i === index ? { ...r, label: e.target.value } : r)),
                        })
                      }
                      placeholder="Monday morning delivery block"
                    />
                  </div>
                </div>
              ))}

              <Button
                variant="soft"
                size="sm"
                onClick={() => setBlockForm({ ...blockForm, rows: [...blockForm.rows, newRow()] })}
              >
                <Plus /> Add another block
              </Button>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="soft" onClick={() => setBlockForm(null)}>
              Cancel
            </Button>
            <Button
              variant="brass"
              disabled={blockMutation.isPending}
              onClick={() => blockForm && blockMutation.mutate(blockForm)}
            >
              {blockMutation.isPending ? "Saving…" : "Save blocks"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>

  );
}

type CalStatus = {
  status: "connected" | "not_connected" | "error";
  email: string | null;
  connectedAt: string | null;
  lastCheckedOkAt: string | null;
  detail: string | null;
};

function fmtWhen(iso: string | null) {
  return iso
    ? new Date(iso).toLocaleString("en-US", { timeZone: "America/Phoenix", dateStyle: "medium", timeStyle: "short" })
    : null;
}

function CalendarBadge({ status, loading }: { status: CalStatus | undefined; loading: boolean }) {
  if (loading) return <p className="mt-2 text-xs text-muted-foreground">Checking Google Calendar…</p>;
  if (!status) return null;
  const tone =
    status.status === "connected"
      ? "bg-success/12 text-success"
      : status.status === "error"
        ? "bg-destructive/12 text-destructive"
        : "bg-muted text-muted-foreground";
  const label =
    status.status === "connected" ? "Calendar connected" : status.status === "error" ? "Calendar expired / error" : "Calendar not connected";
  return (
    <div className="mt-2 space-y-0.5">
      <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${tone}`}>{label}</span>
      {status.email ? <p className="text-xs text-muted-foreground">Google account: {status.email}</p> : null}
      {status.connectedAt ? <p className="text-xs text-muted-foreground">Connected: {fmtWhen(status.connectedAt)}</p> : null}
      {status.lastCheckedOkAt ? <p className="text-xs text-muted-foreground">Last verified: {fmtWhen(status.lastCheckedOkAt)}</p> : null}
      {status.detail ? <p className="text-xs text-destructive">{status.detail}</p> : null}
    </div>
  );
}

function CalendarSummary({
  loading,
  configured,
  needing,
  total,
  onRefresh,
}: {
  loading: boolean;
  configured: boolean;
  needing: (CalStatus & { name: string })[];
  total: number;
  onRefresh: () => void;
}) {
  return (
    <section className="panel mb-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg text-foreground">Google Calendar readiness</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {!configured
              ? "Calendar syncing is not configured on the server."
              : loading && total === 0
                ? "Checking every trainer's connection…"
                : needing.length === 0
                  ? `All ${total} trainers are connected.`
                  : `${needing.length} of ${total} trainers need to connect or reconnect before deploying.`}
          </p>
          {configured && needing.length > 0 ? (
            <p className="mt-2 text-sm text-foreground">
              {needing.map((n) => `${n.name}${n.status === "error" ? " (expired)" : ""}`).join(", ")}
            </p>
          ) : null}
        </div>
        <Button variant="soft" size="sm" onClick={onRefresh} disabled={loading}>
          {loading ? "Checking…" : "Recheck"}
        </Button>
      </div>
    </section>
  );
}
